import axiosInstance from "@/axios";

export interface UserStatusResponse {
  status: number;
  data: {
    isActive: boolean;
    userId: string;
    email: string;
    displayName: string;
    lastLoginAt: string;
    theme: "light" | "dark";
    agencies?: string[]; // Add this
    roles?: string[]; // Add this
  };
  message: string;
  isNoAgenciesError?: boolean; // Special flag for "No agencies assigned" 403 error
}

/**
 * User service for user-related API calls
 */
export const userService = {
  /**
   * Fetches user status including theme preference
   * Called when user is authenticated to sync user preferences
   */
  getUserStatus: async (): Promise<UserStatusResponse> => { // NOSONAR
    try {
      const response = await axiosInstance.get<UserStatusResponse>("/user/status");
      return response.data;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      // Handle 403 Forbidden with "No agencies assigned" message - allow access to dashboard
      if (error.response?.status === 403) {
        const errorData = error.response.data || {};
        const errorMessage = errorData.message || errorData.errorMessage || errorData.error || "";
        const lowerErrorMessage = errorMessage.toLowerCase();
        // Check for "no agencies assigned" error message (case-insensitive)
        // Also check if the error field contains "Forbidden" and message contains "no agencies"
        const hasNoAgenciesMessage = lowerErrorMessage.includes("no agencies assigned") || 
                                     lowerErrorMessage.includes("no agencies assigned to user") ||
                                     (errorData.error === "Forbidden" && lowerErrorMessage.includes("no agencies"));
        
        if (hasNoAgenciesMessage) {
          console.warn("User has no agencies assigned (403) - allowing dashboard access");
          return {
            status: 403,
            data: {
              isActive: true, // Treat as active to allow dashboard access
              userId: errorData.data?.userId || "",
              email: errorData.data?.email || "",
              displayName: errorData.data?.displayName || "",
              lastLoginAt: errorData.data?.lastLoginAt || "",
              theme: errorData.data?.theme || "light",
            },
            message: errorMessage,
            isNoAgenciesError: true, // Flag to identify this special case
          };
        }
      }

      // Handle 423 Locked status - account is inactive
      if (error.response?.status === 423) {
        console.warn("User account is inactive (423)");
        return {
          status: 423,
          data: {
            isActive: false,
            userId: error.response.data?.data?.userId || "",
            email: error.response.data?.data?.email || "",
            displayName: error.response.data?.data?.displayName || "",
            lastLoginAt: error.response.data?.data?.lastLoginAt || "",
            theme: error.response.data?.data?.theme || "light",
          },
          message: error.response.data?.message || "Account is inactive",
        };
      }
      
      console.error("Failed to fetch user status:", error);
      throw error;
    }
  },

  /**
   * Updates user theme preference
   * Called when user changes theme to sync with backend
   */
  updateTheme: async (theme: "light" | "dark"): Promise<void> => {
    try {
      await axiosInstance.patch("/user/settings", { theme });
      console.log(`Theme preference saved to backend: ${theme}`);
    } catch (error) {
      console.error("Failed to update theme preference:", error);
      // Silent failure - don't disrupt user experience
    }
  },
};

